import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { speakText, getVoiceGender, setVoiceGenderPref, warmUpVoices, sttLang } from "@/lib/voice";
import { useLang } from "@/contexts/LanguageContext";
import { Bot, X, Send, Loader2, Mic, MicOff, Volume2, VolumeX, Navigation, User } from "lucide-react";

// Show only on the logged-in dashboard / app routes (never on landing, marketing,
// auth or admin pages).
const APP_PREFIXES = [
    "/dashboard", "/family", "/members", "/add-member", "/family-tree", "/person",
    "/memories", "/video-memories", "/vault", "/legal-vault", "/legacy-vault",
    "/family-health", "/health-repository", "/blood-group-registry", "/timeline",
    "/profile", "/record-story", "/calendar", "/ai/", "/time-capsule", "/privacy-settings",
    "/settings",
];

const SUGGESTIONS = [
    "Add a family member",
    "Open my family tree",
    "Show my Family ID card",
    "Open my document vault",
];

const AUTO_SEND_SECONDS = 30;

const SpeechRecognition = typeof window !== "undefined"
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;

export default function DashboardAssistant() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState(null);
    const [listening, setListening] = useState(false);
    const [speakBack, setSpeakBack] = useState(false);
    const [voiceGender, setVoiceGender] = useState(getVoiceGender);
    const [pending, setPending] = useState(null); // {seconds} — voice review countdown
    const [messages, setMessages] = useState([
        { role: "bot", text: "नमस्ते 🙏 I'm your Family Assistant. Ask me anything — or tell me what you want to do and I'll take you there instantly." },
    ]);
    const scrollRef = useRef(null);
    const recogRef = useRef(null);
    const finalRef = useRef("");
    const tickRef = useRef(null);
    const maxRef = useRef(null);
    const genderRef = useRef(voiceGender);
    const { lang } = useLang();
    const langRef = useRef(lang);

    useEffect(() => { genderRef.current = voiceGender; }, [voiceGender]);
    useEffect(() => { langRef.current = lang; }, [lang]);
    useEffect(() => { warmUpVoices(); }, []);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    const cycleGender = () => setVoiceGender((g) => {
        const n = g === "male" ? "female" : "male";
        setVoiceGenderPref(n);
        return n;
    });

    const speak = useCallback((text) => speakText(text, genderRef.current, sttLang(langRef.current)), []);

    const clearPending = useCallback(() => {
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
        setPending(null);
    }, []);

    const send = useCallback(async (text, viaVoice = false) => {
        const q = (text ?? input).trim();
        if (!q || busy) return;
        clearPending();
        setInput("");
        setMessages((m) => [...m, { role: "user", text: q }]);
        setBusy(true);
        try {
            const { data } = await api.post("/assistant/chat", { session_id: sessionId, message: q }, { timeout: 45000 });
            if (data.session_id && !sessionId) setSessionId(data.session_id);
            setMessages((m) => [...m, { role: "bot", text: data.reply, route: data.route || null }]);
            if (speakBack || viaVoice) speak(data.reply);
            // Do NOT auto-close / auto-navigate — that made the panel "vanish" and
            // felt like no reply. Show the answer + a tappable "Open this page" chip.
        } catch (e) {
            setMessages((m) => [...m, { role: "bot", text: "I couldn't reach the assistant just now. Please try again in a moment." }]);
        } finally {
            setBusy(false);
        }
    }, [input, busy, sessionId, navigate, speak, speakBack, clearPending]);

    // After voice capture, don't fire immediately — show the text and count down.
    const startPending = useCallback((text) => {
        clearPending();
        let s = AUTO_SEND_SECONDS;
        setPending({ seconds: s });
        tickRef.current = setInterval(() => {
            s -= 1;
            if (s <= 0) { clearPending(); send(text, true); }
            else setPending({ seconds: s });
        }, 1000);
    }, [clearPending, send]);

    const toggleMic = useCallback(() => {
        if (!SpeechRecognition) return;
        if (listening) { try { recogRef.current?.stop(); } catch { /* */ } return; }
        try {
            const r = new SpeechRecognition();
            r.lang = sttLang(langRef.current); // matches the user's selected app language
            r.interimResults = true;
            r.continuous = true; // keep listening through pauses
            r.maxAlternatives = 1;
            finalRef.current = "";
            clearPending();
            r.onresult = (ev) => {
                let interim = "";
                for (let i = ev.resultIndex; i < ev.results.length; i++) {
                    const t = ev.results[i][0].transcript;
                    if (ev.results[i].isFinal) finalRef.current += t; else interim += t;
                }
                setInput((finalRef.current + interim).trim());
            };
            r.onend = () => {
                setListening(false);
                if (maxRef.current) { clearTimeout(maxRef.current); maxRef.current = null; }
                const said = finalRef.current.trim();
                if (said) startPending(said);
            };
            r.onerror = () => setListening(false);
            recogRef.current = r;
            setListening(true);
            r.start();
            maxRef.current = setTimeout(() => { try { r.stop(); } catch { /* */ } }, 30000);
        } catch { setListening(false); }
    }, [listening, startPending, clearPending]);

    useEffect(() => () => { clearPending(); if (maxRef.current) clearTimeout(maxRef.current); }, [clearPending]);

    // Gate: only for logged-in users on app routes.
    const onAppRoute = APP_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p));
    if (!user || !onAppRoute) return null;

    return (
        <>
            {!open && (
                <button
                    type="button"
                    data-testid="dashboard-assistant-fab"
                    aria-label="Family Assistant"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-40 right-4 sm:bottom-24 sm:right-6 z-[64] h-14 w-14 rounded-full flex items-center justify-center shadow-[0_12px_36px_rgba(124,58,237,0.5)] hover:shadow-[0_16px_44px_rgba(124,58,237,0.65)] transition-all hover:-translate-y-1 group"
                    style={{ background: "linear-gradient(135deg,#7C3AED 0%,#6D28D9 60%,#4C1D95 100%)" }}
                >
                    <Bot className="relative h-6 w-6 text-white drop-shadow-md" strokeWidth={2.2} />
                    <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#22C55E] ring-2 ring-white animate-pulse" />
                </button>
            )}

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.94 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.94 }}
                        transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed bottom-24 right-4 sm:bottom-24 sm:right-6 z-[64] w-[92vw] sm:w-[380px] h-[540px] max-h-[78vh] bg-white rounded-3xl shadow-[0_28px_80px_rgba(15,23,42,0.3)] border border-[#E5E7EB] flex flex-col overflow-hidden"
                        data-testid="dashboard-assistant-window"
                    >
                        <div className="relative px-4 py-3 flex items-center gap-2" style={{ background: "linear-gradient(135deg,#4C1D95 0%,#6D28D9 55%,#7C3AED 100%)" }}>
                            <div className="h-9 w-9 rounded-full bg-white/25 flex items-center justify-center"><Bot className="h-5 w-5 text-white" /></div>
                            <div className="flex-1 min-w-0 text-white">
                                <div className="font-extrabold text-[15px] leading-tight">Family Assistant</div>
                                <div className="text-[10.5px] text-white/85 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" /> Instant help & navigation</div>
                            </div>
                            <button type="button" onClick={cycleGender} data-testid="dashboard-assistant-voice-gender"
                                title={`Voice: ${voiceGender === "male" ? "Male" : "Female"} (tap to switch)`}
                                className="h-8 px-2 rounded-full bg-white/20 hover:bg-white/35 flex items-center gap-1 text-white text-[11px] font-bold" aria-label="Switch voice gender">
                                <User className="h-3.5 w-3.5" /> {voiceGender === "male" ? "M" : "F"}
                            </button>
                            <button type="button" onClick={() => setSpeakBack((v) => !v)} data-testid="dashboard-assistant-speaker"
                                className="h-8 w-8 rounded-full bg-white/20 hover:bg-white/35 flex items-center justify-center text-white" aria-label="Toggle voice reply">
                                {speakBack ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                            </button>
                            <button type="button" onClick={() => setOpen(false)} data-testid="dashboard-assistant-close"
                                className="h-8 w-8 rounded-full bg-white/20 hover:bg-white/35 flex items-center justify-center text-white" aria-label="Close">
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-[#F5F3FF] to-white" data-testid="dashboard-assistant-messages">
                            {messages.map((m, i) => (
                                <div key={i}>
                                    <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed shadow-sm whitespace-pre-wrap ${
                                            m.role === "user" ? "bg-[#6D28D9] text-white rounded-br-md" : "bg-white border border-[#EDE9FE] text-[#1F2937] rounded-bl-md"
                                        }`}>{m.text}</div>
                                    </div>
                                    {m.role === "bot" && m.route && (
                                        <div className="mt-1.5 flex justify-start">
                                            <button
                                                type="button"
                                                onClick={() => { setOpen(false); navigate(m.route); }}
                                                data-testid="dashboard-assistant-open-route"
                                                className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-white bg-gradient-to-br from-[#6D28D9] to-[#7C3AED] hover:from-[#5b21b6] hover:to-[#6D28D9] rounded-full px-3 py-1.5 shadow-sm transition-all hover:-translate-y-0.5"
                                            >
                                                <Navigation className="h-3.5 w-3.5" /> Open this page
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {busy && (
                                <div className="flex justify-start">
                                    <div className="bg-white border border-[#EDE9FE] rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-sm inline-flex items-center gap-1.5 text-[#6B7280] text-[12px]">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "0ms" }} />
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "120ms" }} />
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#7C3AED] animate-bounce" style={{ animationDelay: "240ms" }} />
                                    </div>
                                </div>
                            )}
                            {messages.length === 1 && (
                                <div className="pt-2">
                                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#6D28D9] mb-1.5">Try</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {SUGGESTIONS.map((s, i) => (
                                            <button key={s} type="button" onClick={() => send(s)} data-testid={`dashboard-assistant-suggest-${i}`}
                                                className="text-[11.5px] px-2.5 py-1 rounded-full bg-white border border-[#7C3AED]/40 text-[#6D28D9] hover:bg-[#F5F3FF] transition-colors">{s}</button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-3 border-t border-[#EDE9FE] bg-white">
                            {pending && (
                                <div data-testid="dashboard-assistant-voice-countdown" className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-[#F5F3FF] border border-[#DDD6FE] px-3 py-1.5">
                                    <span className="text-[11px] text-[#6D28D9] inline-flex items-center gap-1.5 min-w-0">
                                        <Mic className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Sending in {pending.seconds}s — review or edit</span>
                                    </span>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <button type="button" onClick={clearPending} data-testid="dashboard-assistant-voice-edit"
                                            className="text-[11px] px-2 py-1 rounded-full bg-white border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F9FAFB]">Edit</button>
                                        <button type="button" onClick={() => send(input, true)} data-testid="dashboard-assistant-voice-sendnow"
                                            className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#6D28D9] text-white hover:bg-[#5b21b6]">Send now</button>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-end gap-2">
                                {SpeechRecognition && (
                                    <button type="button" onClick={toggleMic} data-testid="dashboard-assistant-mic"
                                        className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center transition-all ${listening ? "bg-[#DC2626] text-white animate-pulse" : "bg-[#F5F3FF] text-[#6D28D9] hover:bg-[#EDE9FE]"}`}
                                        aria-label={listening ? "Stop listening" : "Speak"}>
                                        {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                                    </button>
                                )}
                                <textarea
                                    data-testid="dashboard-assistant-input"
                                    value={input}
                                    onChange={(e) => { setInput(e.target.value); if (pending) clearPending(); }}
                                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                                    rows={1}
                                    placeholder={listening ? "Listening…" : "Ask or tell me what to do…"}
                                    className="flex-1 resize-none rounded-2xl border border-[#E5E7EB] focus:border-[#7C3AED] focus:ring-2 focus:ring-[#C4B5FD]/40 outline-none px-3.5 py-2 text-[13px] max-h-24"
                                />
                                <button type="button" onClick={() => send()} disabled={busy || !input.trim()} data-testid="dashboard-assistant-send"
                                    className="h-10 w-10 shrink-0 rounded-full bg-gradient-to-br from-[#6D28D9] to-[#7C3AED] text-white shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center">
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
